/**
 * FirstDraftVerdict — the one-tap keep / discard control on a LIVE version
 * (plan Unit 1b). Shows the caller's own verdict for that version and the
 * member tally, and revises in place. Members answer for themselves; a
 * superadmin who is not a member records an admin evaluation, labelled as
 * such. "Discard" is feedback only — it never deletes or unpublishes.
 */
import { useEffect, useState } from 'react';
import type { Creation, CreationFeedback, CreationFeedbackVerdict } from '@djgg/creations';
import { useAuth, useServices } from '../services/context.js';

export function FirstDraftVerdict({
  creation,
  version,
  rows,
}: {
  creation: Creation & { id: string };
  version: number;
  /** Every verdict on the creation (all versions) — the caller subscribes once. */
  rows: Array<CreationFeedback & { id: string }>;
}) {
  const services = useServices();
  const { user } = useAuth();
  const [busy, setBusy] = useState<CreationFeedbackVerdict | null>(null);
  const [error, setError] = useState('');
  const [optimisticVerdict, setOptimisticVerdict] = useState<CreationFeedbackVerdict | null>(null);
  const uid = user?.uid ?? '';
  const onVersion = rows.filter((r) => r.versionN === version);
  const storedMine = onVersion.find((r) => r.respondentUid === uid) ?? null;
  useEffect(() => {
    if (storedMine?.verdict === optimisticVerdict) setOptimisticVerdict(null);
  }, [storedMine?.verdict, optimisticVerdict]);
  if (!user) return null;

  const mine = optimisticVerdict === null
    ? storedMine
    : { verdict: optimisticVerdict };
  const memberUids = creation.memberUids ?? [creation.creatorUid];
  const isMember = user.uid === creation.creatorUid || memberUids.includes(user.uid);
  const tally = onVersion.filter((r) => r.respondentRole !== 'admin');
  let keeps = tally.filter((r) => r.verdict === 'keep').length;
  let discards = tally.length - keeps;
  if (optimisticVerdict !== null && isMember) {
    if (storedMine?.respondentRole !== 'admin') {
      if (storedMine?.verdict === 'keep') keeps -= 1;
      if (storedMine?.verdict === 'discard') discards -= 1;
    }
    if (optimisticVerdict === 'keep') keeps += 1;
    else discards += 1;
  }

  const record = (verdict: CreationFeedbackVerdict): void => {
    if (busy) return;
    setError('');
    setBusy(verdict);
    services.callable
      .recordCreationFeedback({ creationId: creation.id, version, verdict })
      .then(() => setOptimisticVerdict(verdict))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not record your verdict.'))
      .finally(() => setBusy(null));
  };

  const button = (verdict: CreationFeedbackVerdict, label: string) => (
    <button
      type="button"
      className={`btn btn--sm ${mine?.verdict === verdict ? 'btn--primary' : 'btn--ghost'}`}
      aria-pressed={mine?.verdict === verdict}
      disabled={busy !== null}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        record(verdict);
      }}
      data-testid={`first-draft-${verdict}-${version}`}
    >
      {busy === verdict ? 'Saving…' : label}
    </button>
  );

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginLeft: 8 }} data-testid={`first-draft-verdict-${version}`}>
      <span className="muted" style={{ fontSize: 12 }}>
        {isMember ? 'Worth keeping?' : 'Admin evaluation:'}
      </span>
      {button('keep', 'Keep iterating')}
      {button('discard', 'Discard')}
      {keeps + discards > 0 && (
        <span className="muted" style={{ fontSize: 12 }} data-testid={`first-draft-tally-${version}`}>
          {keeps} keep · {discards} discard
        </span>
      )}
      {error && <span className="error-text" style={{ fontSize: 12 }}>{error}</span>}
    </span>
  );
}
